import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export interface ExtractedCvData {
  name?: string;
  email?: string;
  location?: string;
  summary?: string;
  skills: Array<{ skill: string; skillNormalized: string; escoUri?: string }>;
  industries?: string[];
  methodologies?: string[];
  leadershipSkills?: string[];
  workExperience?: Array<{
    title: string;
    company: string;
    description?: string;
    startDate?: string;
    endDate?: string;
  }>;
  education?: Array<{ institution: string; degree: string; year?: string }>;
  certifications?: string[];
}

interface CvReviewDialogProps {
  extracted: ExtractedCvData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMerge: () => void;
}

function formatDateRange(startDate?: string, endDate?: string): string {
  if (!startDate && !endDate) return '';
  if (startDate && endDate) return `${startDate} – ${endDate}`;
  if (startDate) return `${startDate} – present`;
  return endDate ?? '';
}

export function CvReviewDialog({ extracted, open, onOpenChange, onMerge }: CvReviewDialogProps) {
  if (!extracted) return null;

  const skillCount = extracted.skills.length;
  const experienceCount = extracted.workExperience?.length ?? 0;
  const certCount = extracted.certifications?.length ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto flex flex-col">
        <SheetHeader className="mb-4">
          <SheetTitle>Review Extracted CV Data</SheetTitle>
          <p className="text-sm text-muted-foreground">
            Found <span className="font-medium text-foreground">{skillCount}</span> skill
            {skillCount !== 1 ? 's' : ''},{' '}
            <span className="font-medium text-foreground">{experienceCount}</span> experience
            {experienceCount !== 1 ? 's' : ''},{' '}
            <span className="font-medium text-foreground">{certCount}</span> certification
            {certCount !== 1 ? 's' : ''}
          </p>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
          {/* Skills section */}
          {skillCount > 0 && (
            <details open className="group">
              <summary className="cursor-pointer select-none text-sm font-semibold text-foreground py-1 list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Skills ({skillCount})
              </summary>
              <div className="mt-2 space-y-2 pl-4">
                {extracted.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {extracted.skills.map((s) => (
                      <Badge
                        key={s.skillNormalized}
                        className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                      >
                        {s.skill}
                      </Badge>
                    ))}
                  </div>
                )}
                {(extracted.methodologies?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Methodologies</p>
                    <div className="flex flex-wrap gap-1.5">
                      {extracted.methodologies!.map((m) => (
                        <Badge
                          key={m}
                          className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                        >
                          {m}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {(extracted.industries?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Industries</p>
                    <div className="flex flex-wrap gap-1.5">
                      {extracted.industries!.map((i) => (
                        <Badge
                          key={i}
                          className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800"
                        >
                          {i}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {(extracted.leadershipSkills?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Leadership</p>
                    <div className="flex flex-wrap gap-1.5">
                      {extracted.leadershipSkills!.map((l) => (
                        <Badge
                          key={l}
                          className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800"
                        >
                          {l}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Work Experience section */}
          {experienceCount > 0 && (
            <details open className="group">
              <summary className="cursor-pointer select-none text-sm font-semibold text-foreground py-1 list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Work Experience ({experienceCount})
              </summary>
              <ul className="mt-2 space-y-3 pl-4">
                {extracted.workExperience!.map((exp, idx) => (
                  <li key={idx} className="text-sm">
                    <p className="font-medium text-foreground">{exp.title}</p>
                    <p className="text-muted-foreground">{exp.company}</p>
                    {(exp.startDate || exp.endDate) && (
                      <p className="text-xs text-muted-foreground">
                        {formatDateRange(exp.startDate, exp.endDate)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Education section */}
          {(extracted.education?.length ?? 0) > 0 && (
            <details open className="group">
              <summary className="cursor-pointer select-none text-sm font-semibold text-foreground py-1 list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Education ({extracted.education!.length})
              </summary>
              <ul className="mt-2 space-y-2 pl-4">
                {extracted.education!.map((edu, idx) => (
                  <li key={idx} className="text-sm">
                    <p className="font-medium text-foreground">{edu.degree}</p>
                    <p className="text-muted-foreground">
                      {edu.institution}
                      {edu.year ? ` · ${edu.year}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* Certifications section */}
          {certCount > 0 && (
            <details open className="group">
              <summary className="cursor-pointer select-none text-sm font-semibold text-foreground py-1 list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Certifications ({certCount})
              </summary>
              <ul className="mt-2 space-y-1 pl-4">
                {extracted.certifications!.map((cert, idx) => (
                  <li key={idx} className="text-sm text-foreground">
                    {cert}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <SheetFooter className="mt-6 gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onMerge}>Merge into Profile</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
